import { fuseAnalyticsClient } from "@appfire/fuse-analytics-client";
import type { MondayAnalytics } from "@appfire/fuse-analytics-client/dist/src/services/monday.service";
import type {
  AnalyticsClientEvent,
  AnalyticsServiceConfig,
} from "@appfire/fuse-analytics-client/dist/src/types/analytics.types";

type PageViewedEvent = AnalyticsClientEvent & {
  page_name: string;
};

type CommandBoxEditedEvent = AnalyticsClientEvent;

type CommandExecutedEvent = AnalyticsClientEvent & {
  execution_status: CommandExecutedStatus;
};

export enum PageName {
  Authorization = "authorization",
  Runner = "runner",
}

enum EventType {
  PageViewed = "PageViewed",
  CommandBoxEdited = "CommandBoxEdited",
  CommandExecuted = "CommandExecuted",
}

enum CommandExecutedStatus {
  Success = "SUCCEEDED",
  Failure = "FAILED",
}

const analyticsConfig: AnalyticsServiceConfig = {
  appKey: import.meta.env.VITE_MONDAY_APP_ID || "INVALID",
  fingerprintAccessKey: import.meta.env.VITE_FINGERPRINT_ACCESS_KEY || "INVALID",
};

export interface AnalyticsServiceType {
  sendPageViewedEvent(pageName: string): Promise<void>;
  sendCommandBoxEdited(): Promise<void>;
  sendCommandExecuted(success: boolean): Promise<void>;
}

export class AnalyticsService implements AnalyticsServiceType {
  private readonly client: MondayAnalytics;

  private firstEdit = true;

  constructor() {
    this.client = fuseAnalyticsClient("Monday", analyticsConfig);
  }

  public sendPageViewedEvent(pageName: PageName) {
    const event: PageViewedEvent = {
      eventType: EventType.PageViewed,
      page_name: pageName,
    };
    return this.sendEvent(event);
  }

  public sendCommandBoxEdited() {
    // We only ever want to track the first edit of the command box in a given user session.
    if (!this.firstEdit) {
      return Promise.resolve();
    }
    this.firstEdit = false;
    const event: CommandBoxEditedEvent = {
      eventType: EventType.CommandBoxEdited,
    };
    return this.sendEvent(event);
  }

  public sendCommandExecuted(success: boolean) {
    const event: CommandExecutedEvent = {
      eventType: EventType.CommandExecuted,
      execution_status: success ? CommandExecutedStatus.Success : CommandExecutedStatus.Failure,
    };
    return this.sendEvent(event);
  }

  protected async sendEvent(event: AnalyticsClientEvent) {
    try {
      await this.client.sendAnalytics(event);
    } catch (err) {
      // Do not rethrow; while this indicates a configuration error, it is not essential for app functionality.
      // Throwing here will cause an infinite loop during result polling, as one negative consequence.
      console.error("Failed to send analytics event:", err);
    }
  }
}
